import { HStack, Modal } from '@musinsa/mwms-ds'
import Text from '@/components/content/Text'
import styled from 'styled-components'
import { colorSystem } from '@/styles/theme'
import Input from '@/components/content/Input'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useSetCapacityDayDelta } from '@/services/capacity/useCapacityQueries'
import { SetCapacityDayDeltaReq } from '@/services/capacity/types'
import MToast from '@/utils/modal/MToast'
import { CapacityDeltaFormSchema, capacityDeltaFormSchema } from '../../schemas/capacityDayDeltaFormSchema'
import MAlert from '@/utils/modal/MAlert'
import { Utils } from '@/utils/Utils'
import { useCutOffDate } from '../../hooks/useCutOffDate'
import { MENU_CODE } from '../../constants'

interface Props {
  warehouseName: string
  warehouseCode: string
  totalCapacity: number
  capacityType: 'B2C' | 'B2B'
  onClose: () => void
}

export function SetDeltaModal({ warehouseName, warehouseCode, totalCapacity, capacityType, onClose }: Props) {
  const { cutOffDate } = useCutOffDate()

  const {
    control,
    handleSubmit,
    formState: { isValid },
  } = useForm<CapacityDeltaFormSchema>({
    resolver: zodResolver(capacityDeltaFormSchema),
    mode: 'onChange',
  })

  const { mutateAsync: setCapacityDayDeltaMutateAsync } = useSetCapacityDayDelta(MENU_CODE)

  const handleFormSubmit = async (form: CapacityDeltaFormSchema) => {
    try {
      const delta = form.changeCapacity - totalCapacity

      const payload: SetCapacityDayDeltaReq = {
        warehouseCode,
        delta,
        capacityType,
        date: cutOffDate,
      }

      await setCapacityDayDeltaMutateAsync(payload)

      MToast.show('케파가 변경되었습니다.')
      onClose()
    } catch (e) {
      MAlert.show(Utils.getApiErrMessage(e, '케파 변경에 실패했습니다.'))
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={
        <HStack gap={8} align="center">
          <Text kind="Headline_20_bold">케파 변경</Text>
          <Text kind="Headline_16_regular" color={colorSystem.text.common.secondary}>
            [{capacityType}] {warehouseName}
          </Text>
        </HStack>
      }
      buttons={[
        { kind: 'secondary', content: '취소', onClick: onClose },
        { kind: 'primary_light', content: '변경', onClick: handleSubmit(handleFormSubmit), disabled: !isValid },
      ]}
    >
      <HStack gap={16} align="center">
        <Text style={{ minWidth: 110 }} kind="Body_14_medium">
          현재 케파
        </Text>
        <Text kind="Body_14_regular">{totalCapacity?.toLocaleString()}</Text>
      </HStack>

      <HStack gap={16} align="flex-start" style={{ marginTop: 16 }}>
        <Label kind="Body_14_medium">변경 케파</Label>
        <Controller
          control={control}
          name="changeCapacity"
          render={({ field: { value, onChange }, fieldState: { error } }) => (
            <div style={{ width: '100%' }}>
              <Input
                type="number"
                block
                size="md"
                placeholder="변경할 케파 입력"
                value={Number.isNaN(value) ? value : value?.toLocaleString()}
                onChange={(v) => {
                  onChange(v === '' ? '' : Number(v))
                }}
              />
              <div style={{ marginTop: 2 }}>
                {error ? (
                  <Text style={{ marginLeft: 4 }} kind="Body_12_medium" color={colorSystem.text.common.error}>
                    {error.message}
                  </Text>
                ) : (
                  <Text kind="Body_12_regular" color={colorSystem.text.common.tertiary}>
                    변경된 케파는 오늘만 적용됩니다.
                  </Text>
                )}
              </div>
            </div>
          )}
        />
      </HStack>
    </Modal>
  )
}

const Label = styled(Text)`
  min-width: 110px;
  padding: 4px 0 4px;

  &::after {
    content: '*';
    color: ${colorSystem.icon.error};
    position: relative;
    left: 2px;
    top: 3px;
  }
`
